import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowForwardIos, MdAdd } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkspace } from '../../context/WorkspaceContext';

const WorkspaceSelector = ({ 
  isExpanded = true, 
  variant = 'desktop', // 'desktop' or 'mobile'
  className = '' 
}) => {
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const workspaceDropdownRef = useRef(null);
  const { workspaces, activeWorkspace, changeActiveWorkspace } = useWorkspace();
  const navigate = useNavigate();

  // Close workspace dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(event.target)) {
        setWorkspaceDropdownOpen(false);
      }
    };

    if (workspaceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [workspaceDropdownOpen]);

  const handleWorkspaceChange = (workspace) => {
    changeActiveWorkspace(workspace);
    setWorkspaceDropdownOpen(false);
  };

  const handleCreateWorkspace = () => {
    setWorkspaceDropdownOpen(false);
    navigate('/dashboard/settings/team');
  };

  if (!activeWorkspace) return null;

  // Mobile variant - full width bar style
  if (variant === 'mobile') {
    return (
      <div className={`relative w-full ${className}`} ref={workspaceDropdownRef}>
        <div
          onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)}
          className="px-4 py-3 bg-gray-700 hover:bg-gray-600 cursor-pointer transition-all duration-200 border-b-2 border-gray-600"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={activeWorkspace.image || `${process.env.PUBLIC_URL}/random/no-image.png`}
                alt={activeWorkspace.name}
                className="h-8 w-8 rounded-full flex-shrink-0"
                onError={(e) => {
                  e.target.src = `${process.env.PUBLIC_URL}/random/no-image.png`;
                }}
              />
              <span className="text-white font-semibold text-base truncate">
                {activeWorkspace.name}
              </span>
            </div>
            <MdArrowForwardIos
              className={`h-4 w-4 text-gray-400 flex-shrink-0 transform transition-transform duration-200 ${
                workspaceDropdownOpen ? 'rotate-90' : ''
              }`}
            />
          </div>
        </div>

        {/* WORKSPACE DROPDOWN - Mobile */}
        <AnimatePresence>
          {workspaceDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full bg-gray-800 shadow-lg border-b border-gray-600 z-50 max-h-80 overflow-y-auto"
            >
              {workspaces.map((workspace) => (
                <div
                  key={workspace.id}
                  onClick={() => handleWorkspaceChange(workspace)}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-700 ${
                    activeWorkspace.id === workspace.id ? 'bg-blue-600 hover:bg-blue-700' : ''
                  }`}
                >
                  <img
                    src={workspace.image || `${process.env.PUBLIC_URL}/random/no-image.png`}
                    alt={workspace.name}
                    className="h-7 w-7 rounded-full flex-shrink-0"
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL}/random/no-image.png`;
                    }}
                  />
                  <span className="text-white text-sm font-medium truncate flex-1">
                    {workspace.name}
                  </span>
                  {activeWorkspace.id === workspace.id && (
                    <span className="text-blue-300 text-lg">✓</span>
                  )}
                </div>
              ))}
              
              {/* Create New Workspace Option */}
              <div
                onClick={handleCreateWorkspace}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700 cursor-pointer transition-colors bg-gray-750 border-t-2 border-gray-600"
              >
                <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <MdAdd className="text-white" size={20} />
                </div>
                <span className="text-white text-sm font-medium">Create New Workspace</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop variant - sidebar style
  return (
    <motion.div 
      layout 
      className={`${isExpanded ? 'mb-4' : 'mx-auto mb-4'} relative ${className}`} 
      ref={workspaceDropdownRef}
    >
      <div
        onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)}
        className={`${
          isExpanded ? 'px-3 py-2' : 'p-2'
        } bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer transition-all duration-200 border-2 border-gray-600 hover:border-gray-500`}
      >
        <div className={`flex items-center ${isExpanded ? 'justify-between gap-2' : 'justify-center'}`}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img
              src={activeWorkspace.image || `${process.env.PUBLIC_URL}/random/no-image.png`}
              alt={activeWorkspace.name}
              className={`${isExpanded ? 'h-6 w-6' : 'h-8 w-8'} rounded-full flex-shrink-0`}
              onError={(e) => {
                e.target.src = `${process.env.PUBLIC_URL}/random/no-image.png`;
              }}
            />
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="nav-link font-semibold text-sm truncate"
              >
                {activeWorkspace.name}
              </motion.span>
            )}
          </div>
          {isExpanded && (
            <MdArrowForwardIos
              className={`h-3 w-3 text-gray-400 flex-shrink-0 transform transition-transform duration-200 ${
                workspaceDropdownOpen ? 'rotate-90' : ''
              }`}
            />
          )}
        </div>
      </div>

      {/* WORKSPACE DROPDOWN - Desktop */}
      <AnimatePresence>
        {workspaceDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute ${isExpanded ? 'left-0 right-0' : 'left-0'} ${
              isExpanded ? 'mt-2' : 'mt-2 w-48'
            } bg-gray-800 rounded-lg shadow-lg border border-gray-600 z-50 max-h-80 overflow-y-auto`}
          >
            {workspaces.map((workspace, index) => (
              <div
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer transition-colors ${
                  activeWorkspace.id === workspace.id ? 'bg-blue-600 hover:bg-blue-700' : ''
                } ${index === 0 ? 'rounded-t-lg' : ''}`}
              >
                <img
                  src={workspace.image || `${process.env.PUBLIC_URL}/random/no-image.png`}
                  alt={workspace.name}
                  className="h-6 w-6 rounded-full flex-shrink-0"
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL}/random/no-image.png`;
                  }}
                />
                <span className="nav-link text-sm font-medium truncate flex-1">
                  {workspace.name}
                </span>
                {activeWorkspace.id === workspace.id && (
                  <span className="ml-auto text-blue-300">✓</span>
                )}
              </div>
            ))}
            
            {/* Create New Workspace Option */}
            <div
              onClick={handleCreateWorkspace}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer transition-colors border-t border-gray-600 rounded-b-lg bg-gray-750"
            >
              <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <MdAdd className="text-white" size={16} />
              </div>
              <span className="nav-link text-sm font-medium">Create New</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WorkspaceSelector;




